import React, { useState, useMemo } from 'react';
import {
  Card,
  Text,
  Button,
  Badge,
} from '@fluentui/react-components';
import {
  mockVASCommitments,
  mockVASMetrics,
  vasFilterCategories,
  matchesFilter,
  VASFilterCategory,
} from '../../data/mockVASData';
import styles from './VASCommandCenter.module.css';

export interface VASCommandCenterProps {
  onCaseSelect?: (caseId: string) => void;
}

/**
 * Formats a raw number as a currency string (e.g., 4000000 → "$4.0M").
 */
function formatCurrency(amount: number): string {
  if (amount >= 1_000_000) {
    const millions = amount / 1_000_000;
    // Use one decimal place for millions
    return `$${millions.toFixed(1)}M`;
  }
  if (amount >= 1_000) {
    const thousands = amount / 1_000;
    return `$${Math.round(thousands)}K`;
  }
  return `$${amount.toLocaleString()}`;
}

const VASCommandCenter: React.FC<VASCommandCenterProps> = ({ onCaseSelect }) => {
  const [activeFilter, setActiveFilter] = useState<VASFilterCategory>('all');

  const filteredCommitments = useMemo(() => {
    return mockVASCommitments.filter((c) => matchesFilter(c, activeFilter));
  }, [activeFilter]);

  const handleFilterClick = (filterKey: VASFilterCategory) => {
    setActiveFilter(filterKey);
  };

  const handleCaseClick = (caseId: string) => {
    onCaseSelect?.(caseId);
  };

  const getRiskBadgeColor = (risk: string): 'danger' | 'warning' | 'success' | 'informative' => {
    switch (risk) {
      case 'Critical':
        return 'danger';
      case 'High':
        return 'warning';
      case 'Medium':
        return 'informative';
      case 'Low':
        return 'success';
      default:
        return 'informative';
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.headerSection}>
        <Text as="h1" className={styles.title}>
          VAS Commitment Command Center
        </Text>
        <Text as="p" className={styles.subtitle}>
          Track VIK, merchant incentives, credits, rebates, and partner obligations across contracts, CRM, finance, and operations.
        </Text>
        <Text as="p" className={styles.positioningLine}>
          Nolij turns VAS commitments into executable workflows — detecting at-risk incentives, VIK credits, rebates, and partner obligations before value is lost.
        </Text>
      </div>

      {/* Metrics Grid - 3×2 */}
      <div className={styles.metricsGrid}>
        {mockVASMetrics.map((metric) => (
          <Card key={metric.label} className={styles.metricCard}>
            <Text className={styles.metricValue}>{metric.value}</Text>
            <Text className={styles.metricLabel}>{metric.label}</Text>
          </Card>
        ))}
      </div>

      {/* Filter Chips */}
      <div className={styles.filterBar}>
        {vasFilterCategories.map((filter) => (
          <Button
            key={filter.key}
            appearance={activeFilter === filter.key ? 'primary' : 'outline'}
            size="small"
            className={activeFilter === filter.key ? styles.filterChipActive : styles.filterChip}
            onClick={() => handleFilterClick(filter.key)}
          >
            {filter.label}
          </Button>
        ))}
      </div>

      {/* At-Risk Commitments Table */}
      <div className={styles.tableSection}>
        <Text className={styles.tableTitle}>At-Risk Commitments</Text>
        <div className={styles.tableWrapper}>
          {filteredCommitments.length === 0 ? (
            <div className={styles.emptyState}>
              No commitments match this filter
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Case ID</th>
                  <th>Partner</th>
                  <th>Commitment Type</th>
                  <th>Allocation</th>
                  <th>Drawn</th>
                  <th>Remaining</th>
                  <th>Days Left</th>
                  <th>Risk</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredCommitments.map((commitment) => (
                  <tr
                    key={commitment.caseId}
                    onClick={() => handleCaseClick(commitment.caseId)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCaseClick(commitment.caseId);
                      }
                    }}
                    tabIndex={0}
                    role="row"
                    style={{ cursor: 'pointer' }}
                    className={styles.clickableRow}
                  >
                    <td>
                      <span className={styles.caseIdLink}>
                        {commitment.caseId}
                      </span>
                    </td>
                    <td>{commitment.partner}</td>
                    <td>{commitment.commitmentType}</td>
                    <td>{formatCurrency(commitment.allocation)}</td>
                    <td>{formatCurrency(commitment.drawn)}</td>
                    <td>{formatCurrency(commitment.remaining)}</td>
                    <td>{commitment.daysLeft}</td>
                    <td>
                      <Badge
                        appearance="filled"
                        color={getRiskBadgeColor(commitment.risk)}
                      >
                        {commitment.risk}
                      </Badge>
                    </td>
                    <td>{commitment.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default VASCommandCenter;
